// Mapper central des résultats de matching (audit P0.6).
// Source de vérité unique pour :
//  - les types de sortie du moteur (match_analysis_json)
//  - le SELECT commun sur la table matches
//  - la conversion ligne DB → MatchResult (avant : dupliquée dans
//    MatchingContext et MissionMatchResults, avec dérives)
//  - la détection des statuts terminaux de matching_tasks

// ─── Types de sortie du moteur ───────────────────────────────────────────────

/** Statuts émis par matching-worker(-rec) — VALIDÉ_PAR_DÉDUCTION vient des équivalences Sprint 1 */
export type CriterionStatus = 'VALIDÉ' | 'VALIDÉ_PAR_DÉDUCTION' | 'PARTIEL' | 'MANQUANT';

export interface CriterionResult {
    criterion_name: string;
    importance: string;
    candidate_status: CriterionStatus;
    comment: string;
}

export interface ObjectionItem {
    potential_objection: string;
    counter_argument: string;
    expected_answer?: string;
}

export interface VerificationQuestion {
    question: string;
    context?: string;
    expected_answer?: string;
}

export interface OutreachContent {
    hook?: string;
    linkedin_dm_short?: string;
    linkedin_dm_full?: string;
    email?: string;
}

/** KNOCKOUT-1 — évaluation par deal-breaker (verdict + preuve) */
export interface DealBreakerVerdict {
    deal_breaker: string;
    verdict: 'respecté' | 'bloquant' | 'à vérifier' | string;
    evidence?: string;
    reasoning?: string;
}

// ─── Étage de contraintes (CONSTRAINT-1) ────────────────────────────────────
// Source de vérité : `supabase/functions/_shared/eligibility-v2.ts` (EligibilityResult,
// DimensionTrace) et le bloc `score_breakdown.adjustments` de
// `supabase/functions/matching-worker-rec/index.ts`.

/** Dimensions évaluées par l'étage de contraintes. */
export type EligibilityDimension = 'role' | 'budget' | 'seniority' | 'availability' | 'location';

/** Gradation de gêne. `blocker` plafonne, il ne retranche pas. */
export type PenaltyLevel = 'none' | 'minor' | 'moderate' | 'major' | 'severe' | 'blocker';

/** Verdict de l'étage de contraintes (CONSTRAINT-1 D8). Les anciens libellés du Gold
 *  Registry — `primary_gold`, `borderline`, `negative`, `excluded` — n'existent plus. */
export type EligibilityLabel = 'valide' | 'avec_reserve' | 'rejete';

/** Provenance d'un plafond. Seuls le TJM et le rôle plafonnent. */
export type CapReason = 'tjm' | 'role';

/** Trace d'une dimension. `points` = retranché ; `cap` = plafond imposé. Une dimension
 *  a l'un OU l'autre, jamais les deux : le rôle et le TJM plafonnent, les autres retranchent. */
export interface EligibilityDimensionTrace {
    dimension: EligibilityDimension | string;
    level: PenaltyLevel | string;
    points: number;
    cap: number | null;
    talent: unknown;
    mission: unknown;
}

/** Décomposition du score écrite par le moteur (score_breakdown) */
export interface ScoreBreakdown {
    risks_to_verify?: string[];
    score_final: number;
    score_calcule: number;
    /** Part LLM du score — le moteur écrit `score_gemini` (jamais `score_gpt`) */
    score_gemini: number;
    is_knockout: boolean;
    knockout_reason: string;
    poids_calcule: string;
    poids_gemini: string;
    bonus_impact: number;
    must_have?: { total: number; valides: number; manquants: number };
    adjustments?: {
        knockout_applied?: boolean;
        knockout_ignored?: boolean;
        knockout_index?: number;
        knockout_overlap_score?: number;
        knockout_matched_db?: string | null;
        domain_cap_applied?: boolean;
        /** true = le plafond a effectivement tronqué le score. À NE PAS confondre avec
         *  `eligibility_cap_reason`, qui est écrit dès qu'un plafond a été CALCULÉ. */
        eligibility_cap_applied?: boolean;
        eligibility_label?: EligibilityLabel | string | null;
        /** Total réellement RETRANCHÉ du score. Vaut 0 sous knockout : l'étage de
         *  contraintes est court-circuité (worker : `if (!knockout && eligibility)`). */
        eligibility_penalty?: number;
        /** Plafond calculé — présent même quand il n'a pas pris la main. */
        eligibility_cap_reason?: CapReason | null;
        /** Ce que le moteur a CALCULÉ, pas ce qu'il a APPLIQUÉ. Croiser avec
         *  `eligibility_penalty` et `eligibility_cap_applied` avant tout affichage. */
        penalty_detail?: Array<{
            dimension: EligibilityDimension | string;
            level: PenaltyLevel | string;
            points: number;
            cap: number | null;
        }>;
    };
    eligibility?: {
        profile: string;
        label: EligibilityLabel | string;
        penalty_points: number;
        score_cap: number | null;
        cap_reason: CapReason | null;
        dimensions: EligibilityDimensionTrace[];
    } | null;
}

export interface MatchResult {
    candidate_id?: number;
    matching_task_id?: number;
    rank?: number;
    name?: string;
    score?: number;
    title?: string;
    recommendation_status?: string;
    placement_status?: string;
    consultant_note?: string;
    availability?: string;
    tjm_min?: number;
    tjm_max?: number;
    /** @deprecated use verdict_teaser for preview, verdict_full for details */
    verdict_short?: string;
    verdict_teaser?: string;
    verdict_full?: string;
    sales_pitch?: string;
    /** Client-facing executive summary — positive only, no caveats */
    client_summary?: string;
    /** Client-facing full pitch — commercial tone, sell mode */
    client_pitch?: string;
    top_achievements?: string[];
    criteria_analysis?: CriterionResult[];
    deal_breakers_analysis?: DealBreakerVerdict[];
    objection_handling?: ObjectionItem[];
    verification_questions?: (string | VerificationQuestion)[];
    outreach?: OutreachContent;
    score_breakdown?: ScoreBreakdown | null;
    knockout_reason?: string;
    contact?: { phone?: string; email?: string; location?: string; linkedin?: string };
    [key: string]: unknown;
}

export interface MatchingRun {
    task_id: number;
    created_at: string;
    matched_count: number;
    candidates: MatchResult[];
    status?: string;
}

// ─── Helpers de statut ───────────────────────────────────────────────────────

/** Un critère compte comme validé qu'il soit prouvé textuellement ou par déduction */
export const isValidatedStatus = (s?: string): boolean =>
    s === 'VALIDÉ' || s === 'VALIDÉ_PAR_DÉDUCTION';

/**
 * Statuts terminaux de matching_tasks.
 * Le moteur écrit `error: <détail>` (tronqué à 255) — jamais `error` seul,
 * d'où le startsWith. `Terminé (N échec(s))` vient d'increment_job_progress.
 */
export const isTerminalTaskStatus = (s?: string | null): boolean => {
    const status = s ?? '';
    return ['Tâche Terminée', 'Done - Pas de résultat'].includes(status)
        || status.startsWith('Terminé (')
        || status.startsWith('error');
};

// ─── Lecture de l'étage de contraintes (CONSTRAINT-1 D9) ────────────────────
//
// LE PIÈGE : `penalty_detail` décrit ce que le moteur a CALCULÉ, pas ce qu'il a
// APPLIQUÉ. Sur la tâche 364, Faridh Badaoui porte `location:major(-20)` alors que
// `eligibility_penalty` vaut 0 : son score de 15 vient d'un knockout, qui court-circuite
// tout l'étage de contraintes. Afficher `penalty_detail` tel quel produirait une
// soustraction mensongère — pire que l'opacité qu'on corrige.
//
// Même piège en plus discret sur `eligibility_cap_reason` : le moteur l'écrit dès qu'un
// plafond a été CALCULÉ, y compris quand il n'a pas pris la main. Seul
// `eligibility_cap_applied` dit qu'il a tronqué le score.
//
// D'où la règle de lecture : un seul mécanisme explique le score, et il se lit dans
// l'ordre de priorité du moteur.

const DIMENSION_LABELS: Record<string, string> = {
    role: 'Rôle',
    budget: 'TJM',
    seniority: 'Séniorité',
    availability: 'Disponibilité',
    location: 'Localisation',
};

const LEVEL_LABELS: Record<string, string> = {
    minor: 'écart léger',
    moderate: 'écart modéré',
    major: 'écart important',
    severe: 'écart majeur',
    blocker: 'rédhibitoire',
};

export const dimensionLabel = (d: string): string => DIMENSION_LABELS[d] ?? d;
export const levelLabel = (l: string): string => LEVEL_LABELS[l] ?? l;

/** Ce qui explique le score final. Un seul mécanisme gagne — celui que le moteur a
 *  réellement appliqué en dernier. */
export type ScoreDriver = 'knockout' | 'cap' | 'penalty' | 'domain' | 'none';

export interface PenaltyLine {
    dimension: string;
    /** « Localisation » */
    label: string;
    level: string;
    /** « écart important » */
    levelLabel: string;
    points: number;
}

export interface ScoreExplanation {
    driver: ScoreDriver;
    /** Phrase courte, prête à afficher. */
    headline: string;
    /** Total réellement retranché. */
    penaltyApplied: number;
    /** Retraits EFFECTIVEMENT appliqués, par dimension. Vide si rien n'a été retranché. */
    penalties: PenaltyLine[];
    /** Score avant retraits — seulement quand la soustraction est vérifiable. */
    scoreBeforePenalty: number | null;
    /** Plafond qui a réellement tronqué le score. */
    cap: { value: number | null; reason: CapReason | 'domain' | 'knockout'; label: string } | null;
    /** Gênes relevées mais NON déduites (plafond ou knockout a pris la main). Sans
     *  points : les afficher chiffrées serait exactement la soustraction mensongère
     *  qu'on cherche à éviter. */
    noted: Array<{ dimension: string; label: string; level: string; levelLabel: string }>;
    /** Verdict de l'étage de contraintes, ou null quand il n'a pas tourné (knockout). */
    eligibilityLabel: EligibilityLabel | null;
    /** true dès qu'il y a quelque chose à montrer — évite le bloc vide. */
    hasContent: boolean;
}

const CAP_LABELS: Record<CapReason | 'domain' | 'knockout', string> = {
    role: 'rôle incompatible avec la mission',
    tjm: 'TJM très au-dessus du budget',
    domain: "domaine d'expertise hors sujet",
    knockout: 'condition rédhibitoire non respectée',
};

const ELIGIBILITY_LABELS: Record<EligibilityLabel, string> = {
    valide: 'Valide',
    avec_reserve: 'Avec réserve',
    rejete: 'Rejeté',
};

export const eligibilityLabelText = (l: EligibilityLabel | null): string =>
    l ? ELIGIBILITY_LABELS[l] : '';

const isEligibilityLabel = (v: unknown): v is EligibilityLabel =>
    v === 'valide' || v === 'avec_reserve' || v === 'rejete';

const toLine = (d: { dimension: string; level: string; points: number }): PenaltyLine => ({
    dimension: d.dimension,
    label: dimensionLabel(d.dimension),
    level: d.level,
    levelLabel: levelLabel(d.level),
    points: d.points,
});

/**
 * Traduit `score_breakdown` en une explication honnête du score affiché.
 *
 * Ordre de priorité, calqué sur le worker :
 *   1. knockout  — `scoreFinal = min(score_gemini, 15)`, l'étage de contraintes ne tourne pas
 *   2. plafond   — `eligibility_cap_applied`, le plafond domine la pénalité (doctrine D4)
 *   3. retrait   — `eligibility_penalty > 0`
 *   4. domaine   — `domain_cap_applied` (plafond à 35, antérieur à CONSTRAINT-1)
 *   5. rien
 */
export function explainScore(sb?: ScoreBreakdown | null): ScoreExplanation {
    const none: ScoreExplanation = {
        driver: 'none', headline: '', penaltyApplied: 0, penalties: [],
        scoreBeforePenalty: null, cap: null, noted: [], eligibilityLabel: null, hasContent: false,
    };
    if (!sb) return none;

    const adj = sb.adjustments ?? {};
    const detail = adj.penalty_detail ?? [];
    const knockout = !!sb.is_knockout;
    const capApplied = !!adj.eligibility_cap_applied;
    const penalty = adj.eligibility_penalty ?? 0;
    const rawLabel = adj.eligibility_label ?? sb.eligibility?.label;

    // Sous knockout, l'étage de contraintes est court-circuité : son verdict décrirait un
    // calcul qui n'a pas eu lieu. Un « Avec réserve » à côté d'un score de 15 se lit comme
    // une contradiction — c'est précisément le genre d'affichage qu'on retire.
    const eligibilityLabel = !knockout && isEligibilityLabel(rawLabel) ? rawLabel : null;

    // Les dimensions retenues comme « relevées » : une gêne réelle, mais dont les points
    // n'ont pas été retranchés. On garde le niveau, jamais les points.
    const notedFrom = (rows: typeof detail) => rows
        .filter(d => d.level !== 'none')
        .map(d => ({
            dimension: d.dimension,
            label: dimensionLabel(d.dimension),
            level: String(d.level),
            levelLabel: levelLabel(String(d.level)),
        }));

    if (knockout) {
        return {
            ...none,
            driver: 'knockout',
            headline: `Écarté : ${CAP_LABELS.knockout}`,
            cap: { value: 15, reason: 'knockout', label: CAP_LABELS.knockout },
            // Les retraits calculés n'ont jamais été appliqués : ils restent des
            // observations, sans chiffre.
            noted: notedFrom(detail),
            eligibilityLabel: null,
            hasContent: true,
        };
    }

    // Retraits réellement appliqués. `points > 0` suffit : une dimension qui plafonne
    // écrit toujours `points: 0`.
    const penalties = penalty > 0 ? detail.filter(d => d.points > 0).map(toLine) : [];

    if (capApplied) {
        const reason: CapReason = adj.eligibility_cap_reason ?? 'role';
        const capValue = sb.eligibility?.score_cap
            ?? detail.find(d => d.dimension === reason && d.cap !== null)?.cap
            ?? null;
        return {
            ...none,
            driver: 'cap',
            headline: capValue !== null
                ? `Score plafonné à ${capValue} — ${CAP_LABELS[reason]}`
                : `Score plafonné — ${CAP_LABELS[reason]}`,
            cap: { value: capValue, reason, label: CAP_LABELS[reason] },
            // Le plafond a écrasé le résultat de la soustraction : afficher les retraits
            // chiffrés donnerait une arithmétique qui ne tombe pas juste (doctrine D4).
            penaltyApplied: penalty,
            noted: notedFrom(detail.filter(d => d.dimension !== reason)),
            eligibilityLabel,
            hasContent: true,
        };
    }

    if (penalty > 0) {
        // La soustraction n'est vérifiable que si le score n'a pas été ramené à 0 par
        // le clamp final du worker.
        const scoreBefore = sb.score_final > 0 ? sb.score_final + penalty : null;
        return {
            ...none,
            driver: 'penalty',
            // Format demandé par D9 : « −8 localisation · −3 séniorité ». Le repli couvre
            // les matchs antérieurs à `penalty_detail`, qui ne portent que le total.
            headline: penalties.length > 0
                ? penalties.map(p => `−${p.points} ${p.label}`).join(' · ')
                : `−${penalty} points`,
            penaltyApplied: penalty,
            penalties,
            scoreBeforePenalty: scoreBefore,
            noted: notedFrom(detail.filter(d => d.points === 0)),
            eligibilityLabel,
            hasContent: true,
        };
    }

    if (adj.domain_cap_applied) {
        return {
            ...none,
            driver: 'domain',
            headline: `Score plafonné à 35 — ${CAP_LABELS.domain}`,
            cap: { value: 35, reason: 'domain', label: CAP_LABELS.domain },
            noted: notedFrom(detail),
            eligibilityLabel,
            hasContent: true,
        };
    }

    // Aucun retrait, aucun plafond. `hasContent` reste false : pas de bloc vide.
    return { ...none, eligibilityLabel };
}

// ─── Les 5 dimensions, en clair (CONSTRAINT-1 D9 — reprise front) ───────────
//
// `score_breakdown.eligibility.dimensions` porte TOUJOURS les 5 dimensions avec les
// valeurs comparées (vérifié : 53/53 sur les tâches 364-365), là où `penalty_detail`
// ne garde que celles qui coûtent. C'est donc la seule source qui permet de montrer
// « ce qui a été regardé », et pas seulement « ce qui a fait mal ».
//
// TROIS ÉTATS, pas deux. Le moteur ne sanctionne jamais une donnée absente : une
// dimension à `none` peut vouloir dire « ça colle » ou « on ne sait pas ». Les
// confondre affiche un feu vert sur une fiche incomplète — la fiche 354 d'Axel n'a
// aucune localisation et ressortait comme un critère satisfait.

/** `ok` = comparé et conforme · `cost` = a coûté · `unknown` = donnée absente d'un côté. */
export type DimensionState = 'ok' | 'cost' | 'unknown';

export interface DimensionRow {
    dimension: string;
    label: string;
    level: string;
    levelLabel: string;
    state: DimensionState;
    /** Valeur candidat, déjà formatée. Vide = absente. */
    talent: string;
    /** Valeur mission, déjà formatée. Vide = absente. */
    mission: string;
    /** Ce qui a été APPLIQUÉ : « −8 », « plafond 70 », « non appliqué », ou ''. */
    impact: string;
}

const ROLE_TAG_LABELS: Record<string, string> = {
    IT_PILOTAGE: 'Pilotage', IT_MANAGEMENT: 'Management', IT_PROJET: 'Projet',
    IT_PRODUCT: 'Product', IT_MOA: 'MOA', IT_APPLICATIONS: 'Applications',
    IT_ARCHITECTURE: 'Architecture', IT_DATA: 'Data', IT_INFRA: 'Infrastructure',
    IT_CYBER: 'Cybersécurité', IT_SUPPORT: 'Support', IT_FOURNISSEURS: 'Fournisseurs',
};

/** Échelle de séniorité du moteur (`SEN_SCALE` dans eligibility-v2). */
const SENIORITY_LABELS: Record<number, string> = {
    1: 'Junior', 2: 'Confirmé', 3: 'Senior', 4: 'Expert', 5: 'Directeur',
};

/** Le moteur compare des objets `GeoLocation`, des tableaux de tags, des nombres et des
 *  chaînes libres. Chacun a besoin de son propre rendu — d'où le formatage par dimension
 *  plutôt qu'un `String(v)` générique, qui produirait « [object Object] » sur la géo. */
function formatValue(dimension: string, v: unknown): string {
    if (v === null || v === undefined || v === '') return '';
    switch (dimension) {
        case 'role': {
            if (!Array.isArray(v) || v.length === 0) return '';
            return v.map(t => ROLE_TAG_LABELS[String(t).toUpperCase()] ?? String(t)).join(' · ');
        }
        case 'budget': {
            const n = Number(v);
            return Number.isFinite(n) && n > 0 ? `${n} €/j` : '';
        }
        case 'seniority': {
            if (typeof v === 'number') return SENIORITY_LABELS[v] ?? `niveau ${v}`;
            return String(v);
        }
        case 'location': {
            if (typeof v === 'string') return v;
            const g = v as { city?: string | null; label?: string | null; resolved?: boolean | null };
            const name = g.city || g.label || '';
            // Adresse fournie mais introuvable en base française : c'est une information
            // sur le candidat, pas une absence de donnée.
            if (!name) return '';
            return g.resolved === false ? `${name} (hors France ?)` : name;
        }
        default:
            return String(v);
    }
}

// ─── La chaîne de calcul complète ───────────────────────────────────────────
//
// Les 5 dimensions ne sont que le DERNIER étage. Le score vient d'abord d'un mélange
// pondéré entre la note des critères et celle du LLM (60/40 par défaut, 0/100 sous
// knockout — le worker écrit les poids appliqués). Montrer les seules contraintes sous
// le titre « comment le score s'est construit » laissait croire qu'elles faisaient tout.

export interface ScoreChain {
    /** Note issue des critères pondérés (MUST=3, NICE=1, BONUS=0,5) et son poids. */
    criteria: { score: number; weight: number };
    /** Note du LLM et son poids. */
    ia: { score: number; weight: number };
    /** Mélange des deux, avant l'étage de contraintes. */
    blended: number;
    /** Score affiché au bout de la chaîne. */
    final: number;
    /** true quand le mélange a été écrasé en aval (knockout, plafond, retrait). */
    adjusted: boolean;
}

/** « 60% » → 0.6. Le worker écrit ces poids en texte. */
const parseWeight = (s?: string): number => {
    const n = parseFloat(String(s ?? '').replace('%', ''));
    return Number.isFinite(n) ? n / 100 : 0;
};

export function scoreChain(sb?: ScoreBreakdown | null): ScoreChain | null {
    if (!sb) return null;
    const wc = parseWeight(sb.poids_calcule);
    const wg = parseWeight(sb.poids_gemini);
    if (wc + wg === 0) return null; // poids absents : matchs antérieurs, on ne devine pas

    const criteria = sb.score_calcule ?? 0;
    const ia = sb.score_gemini ?? 0;
    const blended = Math.round(criteria * wc + ia * wg);
    const final = sb.score_final ?? blended;

    return {
        criteria: { score: criteria, weight: wc },
        ia: { score: ia, weight: wg },
        blended,
        final,
        adjusted: blended !== final,
    };
}

/**
 * Les 5 dimensions prêtes à afficher, avec l'impact RÉELLEMENT appliqué.
 *
 * La colonne impact suit la même règle que `explainScore` : sous knockout ou sous un
 * plafond, une gêne calculée n'a pas été retranchée — elle vaut « non appliqué », jamais
 * un chiffre. Afficher « −20 » sur un knockouté serait la soustraction mensongère que D9
 * corrige.
 */
export function dimensionRows(sb?: ScoreBreakdown | null): DimensionRow[] {
    const dims = sb?.eligibility?.dimensions ?? [];
    if (dims.length === 0) return [];

    const adj = sb?.adjustments ?? {};
    const knockout = !!sb?.is_knockout;
    const capApplied = !!adj.eligibility_cap_applied;
    const capReason = adj.eligibility_cap_reason ?? null;
    const penaltyApplied = (adj.eligibility_penalty ?? 0) > 0;

    return dims.map((d) => {
        const level = String(d.level ?? 'none');
        const talent = formatValue(d.dimension, d.talent);
        const mission = formatValue(d.dimension, d.mission);

        // Une dimension à `none` sans valeur des deux côtés n'a pas été évaluée : le
        // moteur s'est abstenu faute de donnée, il n'a pas constaté une conformité.
        const state: DimensionState =
            level !== 'none' ? 'cost'
            : (!talent || !mission) ? 'unknown'
            : 'ok';

        let impact = '';
        if (level !== 'none') {
            if (knockout) {
                impact = 'non appliqué';
            } else if (capApplied && d.dimension === capReason) {
                impact = `plafond ${d.cap ?? sb?.eligibility?.score_cap ?? ''}`.trim();
            } else if (d.points > 0 && penaltyApplied && !capApplied) {
                impact = `−${d.points}`;
            } else {
                // Gêne réelle, mais le score n'en porte pas la trace : soit un plafond
                // plus bas a tout écrasé, soit le plafond de cette dimension n'a pas mordu.
                impact = 'non appliqué';
            }
        }

        return {
            dimension: d.dimension,
            label: dimensionLabel(d.dimension),
            level,
            levelLabel: levelLabel(level),
            state,
            talent,
            mission,
            impact,
        };
    });
}

// ─── Découpage du verdict IA ────────────────────────────────────────────────
//
// `verdict_full` arrive en un seul bloc de ~565 caractères, 4 à 6 phrases enchaînées.
// On l'AÈRE, on ne le CLASSE pas : sur 16 212 verdicts, 36 % portent deux marqueurs
// d'opposition concurrents, et le motif « Bien qu'il n'ait pas X, ses responsabilités
// sont transférables » présente un POINT FORT en concession. Un découpage
// forts/réserves se tromperait environ une fois sur trois. Le vrai éclaté doit venir
// du schéma de réponse du moteur, pas d'une heuristique de mots-clés ici.

/** Fins de fragment qui ne sont PAS des fins de phrase : initiales (« M. Dupont ») et
 *  titres de civilité. Volontairement court — « etc. » et « cf. » terminent souvent une
 *  phrase pour de bon, les recoller ferait plus de dégâts que le découpage. */
const ABBREV_END = /(?:^|\s)(?:[A-ZÀ-Þ]|Mme|Mlle|Dr|Pr)\.$/u;

/** Découpe un texte en phrases. Purement typographique : aucune interprétation. */
export function splitSentences(text?: string | null): string[] {
    if (!text) return [];
    // Coupe après . ! ? suivis d'un espace et d'une majuscule. Les décimales sont déjà
    // épargnées (« 0.45 » n'est pas suivi d'une capitale), mais pas les initiales : la
    // majuscule qui suit « M. » en est une. D'où le recollage ci-dessous.
    const parts = text.split(/(?<=[.!?])\s+(?=[A-ZÀ-ÖØ-Þ])/u);
    const out: string[] = [];
    for (const part of parts) {
        const prev = out[out.length - 1];
        if (prev !== undefined && ABBREV_END.test(prev)) out[out.length - 1] = `${prev} ${part.trim()}`;
        else out.push(part.trim());
    }
    return out.map(s => s.trim()).filter(Boolean);
}

// ─── SELECT commun ───────────────────────────────────────────────────────────

export const MATCH_ROW_SELECT =
    'candidate_id, matching_task_id, match_score, match_analysis_json, status, placement_status, consultant_note, candidates(full_name, summary_json, availability_status, availability_type, availability_date, tjm_min, tjm_max)';

// ─── Mapping ligne DB → MatchResult ─────────────────────────────────────────

/**
 * Convertit une ligne `matches` (avec jointure `candidates`) en MatchResult.
 * Tolère les deux générations de summary_json (clés n8n historiques et clés create-cv).
 */
export function mapMatchRow(m: any, idx: number): MatchResult {
    const analysis = m.match_analysis_json ?? {};
    const sj = m.candidates?.summary_json ?? {};
    const rawAch = sj.Top_achievements ?? sj.top_achievements;
    let parsedAch: any = rawAch;
    if (typeof rawAch === 'string' && rawAch.trim().startsWith('[')) {
        try { parsedAch = JSON.parse(rawAch); } catch { parsedAch = rawAch; }
    }
    const topAchievements: string[] = Array.isArray(parsedAch)
        ? parsedAch.slice(0, 3).map(String)
        : (typeof parsedAch === 'string' && parsedAch ? [parsedAch] : []);
    return {
        candidate_id: m.candidate_id,
        matching_task_id: m.matching_task_id,
        rank: idx + 1,
        name: m.candidates?.full_name,
        title: sj.Suggested_title ?? sj.suggested_title ?? sj.title ?? sj.current_title,
        score: m.match_score,
        recommendation_status: m.status,
        placement_status: m.placement_status ?? undefined,
        consultant_note: m.consultant_note ?? undefined,
        availability: m.candidates?.availability_status ?? sj.availability ?? sj.disponibilite ?? sj.disponibility ?? sj.available_from ?? sj.Availability ?? sj.disponible_le ?? undefined,
        tjm_min: (() => { const v = m.candidates?.tjm_min ?? sj.tjm_min ?? sj.TJM_min ?? sj.daily_rate_min ?? sj.tarif_min; return v && v > 0 ? v : undefined; })(),
        tjm_max: (() => { const v = m.candidates?.tjm_max ?? sj.tjm_max ?? sj.TJM_max ?? sj.daily_rate_max ?? sj.tarif_max; return v && v > 0 ? v : undefined; })(),
        verdict_short: analysis.verdict_short,
        verdict_teaser: analysis.verdict_teaser,
        verdict_full: analysis.verdict_full,
        sales_pitch: analysis.outreach?.email ?? analysis.sales_pitch_email,
        client_summary: analysis.client_summary,
        client_pitch: analysis.client_pitch,
        top_achievements: topAchievements,
        criteria_analysis: analysis.criteria_analysis ?? [],
        deal_breakers_analysis: analysis.deal_breakers_analysis ?? [],
        objection_handling: analysis.objection_handling ?? [],
        verification_questions: analysis.verification_questions ?? [],
        outreach: analysis.outreach,
        score_breakdown: analysis.score_breakdown ?? null,
        knockout_reason: analysis.score_breakdown?.knockout_reason ?? '',
        contact: {
            phone:    sj.Contact_phone    ?? sj.phone    ?? sj.contactInfo?.phone,
            email:    sj.Contact_email    ?? sj.email    ?? sj.contactInfo?.email,
            location: sj.contactInfo?.location_city ?? sj.location ?? sj.city,
            linkedin: sj.Contact_linkedin ?? sj.linkedin ?? sj.contactInfo?.linkedin_url,
        },
    };
}
